import { Shdr, shdr_type } from 'twrk/Shdr.js';
import { wbgl_type } from 'twrk/Wbgl.js';
import { scrn_type } from 'twrk/Scrn.js';
import { gl_Image } from 'twrk/gl_Image.js';

//a fullscreen quad sampling the background image with CSS background-size:cover math: the UV
//rect is cropped on whichever axis the screen is relatively wider/taller than the image, so the
//image always fills the canvas without stretching. Drawn first, before anything is cleared by
//other layers, so it sits behind the scene like a photo backdrop.

let _shader: shdr_type | null = null;

function get_shader(Canvas: wbgl_type): shdr_type {
    if (!_shader) {
        _shader = Shdr.make(Canvas, {
            id: "background_2d",
            vertex: `#version 300 es
            precision mediump float;
            in vec2 position;
            in vec2 uv;
            out vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = vec4(position, 0.0, 1.0);
            }
            `,
            fragment: `#version 300 es
            precision mediump float;
            uniform sampler2D u_texture;
            uniform float u_time;
            in vec2 vUv;
            out vec4 fragColor;

            //cheap 2D value noise (hash-based), stands in for simplex: smooth enough for a subtle
            //crowd wiggle without needing a real gradient-noise implementation on the GPU
            float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
            
            float noise(vec2 p) {
                vec2 i = floor(p), f = fract(p);
                float a = hash(i), b = hash(i + vec2(1.0, 0.0));
                float c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
                vec2 u = f * f * (3.0 - 2.0 * f);
                return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
            }

            void main() {
                //two octaves of slowly-drifting noise, displacing the sample point a little so the
                //crowd looks like it's shifting/breathing rather than a static photo
                vec2 cell = vUv * vec2(10.0, 10.0);
                float n1 = noise(cell + vec2(u_time * 0.5, u_time * 0.5));
                float n2 = noise(cell * 2.0 + vec2(-u_time * 5.0, u_time * 5.0));
                vec2 wiggle = vec2(n1 - 0.5, n2 - 0.5) * 0.005;
                fragColor = texture(u_texture, vUv + wiggle);
            }
            `
        });
    }
    return _shader;
}

export async function background_load(Canvas: wbgl_type, src: string) {
    await gl_Image.load(Canvas, src);
}

export function background_make(Canvas: wbgl_type, Scrn: scrn_type, src: string) {

    const gl = Canvas.context;
    const shader = get_shader(Canvas);
    const prog = shader.compiled.program;

    const u_texture = gl.getUniformLocation(prog, "u_texture");
    const u_time = gl.getUniformLocation(prog, "u_time");

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buf_pos = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf_pos);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, -1, 1, 1, -1, 1]), gl.STATIC_DRAW);
    const loc_pos = gl.getAttribLocation(prog, "position");
    gl.enableVertexAttribArray(loc_pos);
    gl.vertexAttribPointer(loc_pos, 2, gl.FLOAT, false, 0, 0);
    const buf_uv = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf_uv);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(12), gl.DYNAMIC_DRAW);
    const loc_uv = gl.getAttribLocation(prog, "uv");
    gl.enableVertexAttribArray(loc_uv);
    gl.vertexAttribPointer(loc_uv, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    const image = new Image();
    image.src = src;
    let img_aspect = 1;
    image.onload = () => { img_aspect = image.width / image.height; update_uv(); };

    const update_uv = () => {
        const screen_aspect = Scrn.width / Scrn.height;
        let u0 = 0, u1 = 1, v0 = 0, v1 = 1;
        if (screen_aspect > img_aspect) {
            //screen relatively wider than the image: crop top/bottom
            const visible = img_aspect / screen_aspect;
            v0 = (1 - visible) * 0.5;
            v1 = 1 - v0;
        } else {
            //screen relatively taller than the image: crop left/right
            const visible = screen_aspect / img_aspect;
            u0 = (1 - visible) * 0.5;
            u1 = 1 - u0;
        }
        const uvs = new Float32Array([u0, v1, u1, v1, u0, v0, u1, v1, u1, v0, u0, v0]);
        gl.bindBuffer(gl.ARRAY_BUFFER, buf_uv);
        gl.bufferData(gl.ARRAY_BUFFER, uvs, gl.DYNAMIC_DRAW);
    };
    const off_resize = Scrn.on_resize(update_uv);

    return {
        render: function (time_s: number) {
            gl.useProgram(prog);
            gl.bindVertexArray(vao);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, gl_Image.list[src]);
            gl.uniform1i(u_texture, 0);
            gl.uniform1f(u_time, time_s);
            gl.drawArrays(gl.TRIANGLES, 0, 6);
            gl.bindVertexArray(null);
        },
        kill: function () {
            off_resize();
            gl.deleteBuffer(buf_pos);
            gl.deleteBuffer(buf_uv);
            gl.deleteVertexArray(vao);
        },
    };
}
